import { useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Paper,
    IconButton,
    Button,
} from "@mui/material";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import CloseIcon from "@mui/icons-material/Close";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useGetTicketsChatsById } from "../../assets/nodeApi/getApi/getCommonApi";
import { postTicketChat } from "../../assets/nodeApi/postApi/postCommonApi";
import { patchMarkAllRead } from "../../assets/nodeApi/patchApi/patchCommonApi";
import useAuthStore from "../../store/useAuthStore";
import { useToaster } from "../utils/ToasterProvider";
import { socket } from "../../socket";
import { TextFormField } from "./formfields/TextFormField";
import { getFileUrl } from "../utils/getFileUrl";
import MainDialogBox from "./Dialog/MainDialogBox";


const ChatModule = ({ selectedTicket, onClose, receiverId }) => {
    const { user } = useAuthStore();
    const { showToaster } = useToaster();
    const queryClient = useQueryClient();
    const [messages, setMessages] = useState([]);
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    const { data: chatData, isLoading } = useGetTicketsChatsById(selectedTicket?.id);

    // Reset messages when selected ticket changes
    useEffect(() => {
        setMessages([]);
    }, [selectedTicket?.id]);

    // Update messages when chat data is loaded
    useEffect(() => {
        if (chatData?.data) {
            setMessages(chatData.data);
        }
    }, [chatData]);

    const mutationSendChat = useMutation({
        mutationFn: (formData) => postTicketChat(formData, { headers: { "Content-Type": "multipart/form-data" } }),
    });

    const mutationMarkAsRead = useMutation({
        mutationFn: (id) => patchMarkAllRead(id),
    });

    // ✅ Join ticket room and load messages
    useEffect(() => {
        const ticketId = selectedTicket?.id;
        const userId = user?.id;
        if (!ticketId || !userId) return;

        socket.emit("joinTicket", { ticketId, userId });
        mutationMarkAsRead.mutate(ticketId);

        const handleNewMessage = (newMsg) => {
            if (newMsg.ticketId === ticketId) {
                setMessages((prev) => {
                    // Try to find a corresponding temporary message to replace
                    if (newMsg.senderId == userId) {
                        const tempIndex = prev.findIndex(m => m.isSending && m.messageType === newMsg.messageType);
                        if (tempIndex !== -1) {
                            const newMessages = [...prev];
                            newMessages[tempIndex] = newMsg;
                            return newMessages;
                        }
                    }
                    // Prevent duplicate real messages
                    if (prev.some(m => m.id === newMsg.id)) return prev;
                    return [...prev, newMsg];
                });
            }
        };

        socket.on("newMessage", handleNewMessage);

        return () => {
            socket.emit("leaveTicket", { ticketId, userId });
            socket.off("newMessage", handleNewMessage);
        };
    }, [selectedTicket?.id, user?.id]);

    // ✅ Determine message type
    const getMessageType = (file) => {
        if (!file) return "text";
        if (file.type.startsWith("image/")) return "image";
        return "file";
    };

    // ✅ Handle chat submit
    const handleSubmitChat = async (values, { resetForm, setSubmitting }) => {
        try {
            const ticketId = selectedTicket?.id;
            const type = selectedFile ? getMessageType(selectedFile) : "text";

            // ⬇️ Prevent sending both text and file together
            if (!selectedFile && !values.message.trim()) {
                showToaster("Warning", "Please enter a message or upload a file", "warning");
                return;
            }

            const tempMessageId = Date.now().toString();
            const tempMessage = {
                id: tempMessageId,
                ticketId,
                senderId: user?.id,
                messageType: type,
                message: selectedFile ? null : values.message.trim(),
                createdAt: new Date().toISOString(),
                isSending: true,
                attachmentUrl: selectedFile ? URL.createObjectURL(selectedFile) : null,
                fileName: selectedFile ? selectedFile.name : null,
                sender: { username: user?.username || "You" },
            };

            setMessages((prev) => [...prev, tempMessage]);

            const formData = new FormData();
            formData.append("ticketId", ticketId);
            formData.append("receiverId", receiverId ?? selectedTicket?.createdBy?.id);
            formData.append("messageType", type);

            if (selectedFile) {
                formData.append("file", selectedFile);
                formData.append("message", null);
            } else {
                formData.append("message", values.message.trim());
            }

            // Immediately clear input fields
            resetForm();
            setSelectedFile(null);

            await mutationSendChat.mutateAsync(formData, {
                onSuccess: () => {
                    socket.emit("sendMessage", { ticketId, messageType: type });
                    setSubmitting(false);
                    // Invalidate the query to fetch the latest messages from the API immediately
                    queryClient.invalidateQueries({ queryKey: ["ticket-chat/messages", ticketId] });
                },
                onError: (err) => {
                    showToaster("Error", err?.response?.data?.error || "Failed to send", "error");
                    setSubmitting(false);
                    setMessages((prev) => prev.filter((msg) => msg.id !== tempMessageId));
                },
            });
        } catch (err) {
            console.error("Send error:", err);
            setSubmitting(false);
        }
    };

    const messagesEndRef = useRef(null); // ref to scroll to bottom

    // Auto-scroll whenever messages change
    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
        }
    }, [messages]);

    return (
        <Box
            sx={{
                position: "fixed",
                right: 0,
                top: 0,
                height: "100vh",
                width: "400px",
                bgcolor: "#fff",
                borderLeft: "1px solid #ddd",
                p: 2,
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Header */}
            <Box sx={{ position: "relative", p: 2 }}>
                <IconButton
                    sx={{
                        position: "absolute",
                        top: "20px",
                        right: "5px",
                        bgcolor: "text.secondary",
                        p: 0.1,
                        borderRadius: "50%",
                        boxShadow: 2,
                        "&:hover": { backgroundColor: "text.secondary" },
                    }}
                    onClick={onClose}
                >
                    <CloseIcon sx={{ color: "#000", fontSize: "25px" }} />
                </IconButton>
            </Box>

            <Typography variant="h6" mb={2}>
                Chat for Ticket #{selectedTicket?.ticketId}
            </Typography>

            {/* Messages */}
            {/* <Paper sx={{ flexGrow: 1, p: 2, overflowY: "auto", mb: 2 }}>
                {isLoading && <Typography>Loading chat...</Typography>}

                {!isLoading &&
                    messages.map((msg, i) => {
                        const isMyMsg = msg.senderId == user?.id;
                        const isImage = msg.messageType === "image";
                        const isFile = msg.messageType === "file";

                        return (
                            <Box
                                key={msg.id || i}
                                sx={{
                                    mb: 1.5,
                                    textAlign: isMyMsg ? "right" : "left",
                                }}
                            >
                                <Typography variant="caption" sx={{ display: "block" }}>
                                    {isMyMsg ? "You" : msg.sender?.username || "Unknown"} •{" "}
                                    {new Date(msg.createdAt).toLocaleTimeString([], {
                                        hour: "2-digit",
                                        minute: "2-digit",
                                    })}
                                </Typography>

                                {isImage ? (
                                    <img
                                        src={getFileUrl(msg.attachmentUrl)}
                                        alt="uploaded"
                                        style={{
                                            maxWidth: "60%",
                                            borderRadius: "8px",
                                            marginTop: "5px",
                                        }}
                                    />
                                ) : isFile ? (
                                    <a
                                        href={getFileUrl(msg.attachmentUrl)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            color: "blue",
                                            textDecoration: "underline",
                                        }}
                                    >
                                        📎 Download File
                                    </a>
                                ) : (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            display: "inline-block",
                                            bgcolor: isMyMsg ? "blue.main" : "grey.300",
                                            color: isMyMsg ? "white" : "black",
                                            px: 1.5,
                                            py: 0.5,
                                            borderRadius: 2,
                                            maxWidth: "80%",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {msg.message}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
            </Paper> */}

            <Paper
                sx={{
                    flexGrow: 1,
                    p: 2,
                    overflowY: "auto",
                    mb: 2,
                    display: "flex",
                    flexDirection: "column",
                    height: 400, // or whatever height you need
                }}
            >
                {isLoading && <Typography>Loading chat...</Typography>}

                {!isLoading && (
                    <>
                        {messages.map((msg, i) => {
                            const isMyMsg = msg.senderId == user?.id;
                            const isImage = msg.messageType === "image";
                            const isFile = msg.messageType === "file";
                            const isSending = msg.isSending;

                            return (
                                <Box
                                    key={msg.id || i}
                                    sx={{
                                        mb: 1.5,
                                        textAlign: isMyMsg ? "right" : "left",
                                        opacity: isSending ? 0.6 : 1, // Faded to indicate sending state
                                    }}
                                >
                                    <Typography variant="caption" sx={{ display: "block", color: isSending ? "text.secondary" : "inherit" }}>
                                        {isMyMsg ? "You" : msg.sender?.username || "Unknown"} •{" "}
                                        {isSending ? "Sending..." : new Date(msg.createdAt).toLocaleTimeString([], {
                                            hour: "2-digit",
                                            minute: "2-digit",
                                        })}
                                    </Typography>

                                    {isImage ? (
                                        <img
                                            src={isSending ? msg.attachmentUrl : getFileUrl(msg.attachmentUrl)}
                                            alt={isSending ? "uploading" : "uploaded"}
                                            onClick={() => setPreviewImage(isSending ? msg.attachmentUrl : getFileUrl(msg.attachmentUrl))}
                                            style={{
                                                maxWidth: "200px", // Increased max width/height to make it look better
                                                maxHeight: "200px",
                                                width: "100%",
                                                height: "auto",
                                                borderRadius: "8px",
                                                marginTop: "5px",
                                                cursor: "pointer",
                                                objectFit: "cover",
                                                opacity: isSending ? 0.7 : 1,
                                            }}
                                        />
                                    ) : isFile ? (
                                        isSending ? (
                                            <Typography
                                                variant="body2"
                                                sx={{ color: "blue", textDecoration: "underline" }}
                                            >
                                                📎 Uploading: {msg.fileName}
                                            </Typography>
                                        ) : (
                                            <a
                                                href={getFileUrl(msg.attachmentUrl)}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    color: "blue",
                                                    textDecoration: "underline",
                                                }}
                                            >
                                                📎 Download File
                                            </a>
                                        )
                                    ) : (
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                display: "inline-block",
                                                bgcolor: isMyMsg ? "blue.main" : "grey.300",
                                                color: isMyMsg ? "white" : "black",
                                                px: 1.5,
                                                py: 0.5,
                                                borderRadius: 2,
                                                maxWidth: "80%",
                                                wordBreak: "break-word",
                                            }}
                                        >
                                            {msg.message}
                                        </Typography>
                                    )}
                                </Box>
                            );
                        })}
                        {/* Invisible element to scroll to bottom */}
                        <div ref={messagesEndRef} />
                    </>
                )}
            </Paper>
            {/* Chat Input */}
            {selectedTicket?.status === "CLOSED" ? (
                <Typography>Ticket Closed</Typography>
            ) : (
                <Box mb={"2rem"}>
                    <Formik
                        initialValues={{ message: "" }}
                        validationSchema={Yup.object({ message: Yup.string() })}
                        onSubmit={handleSubmitChat}
                    >
                        {({ setFieldValue, values }) => (
                            <Form>
                                <Box display="flex" alignItems="center" gap={1}>
                                    {/* File Upload */}
                                    {/* <IconButton
                                        component="label"
                                        sx={{
                                            bgcolor: "grey.200",
                                            "&:hover": { bgcolor: "grey.300" },
                                            mt: "15px",

                                        }}
                                        disabled={!!values.message.trim()} // disable file if typing
                                    >
                                        <AttachFileIcon />
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                setSelectedFile(file || null);
                                                setFieldValue("message", "");
                                            }}
                                        />
                                    </IconButton> */}

                                    <IconButton
                                        component="label"
                                        sx={{
                                            bgcolor: "grey.200",
                                            "&:hover": { bgcolor: "grey.300" },
                                            mt: "15px",
                                        }}
                                        disabled={!!values.message.trim()} // disable file if typing
                                    >
                                        <AttachFileIcon />
                                        <input
                                            type="file"
                                            hidden
                                            onChange={(e) => {
                                                const file = e.target.files[0];
                                                if (file) {
                                                    const maxSize = 2 * 1024 * 1024; 
                                                    if (file.size > maxSize) {
                                                        showToaster(
                                                            "Warning",
                                                            "File size exceeds 2 MB limit",
                                                            "warning"
                                                        );
                                                        e.target.value = ""; 
                                                        return;
                                                    }
                                                    setSelectedFile(file);
                                                    setFieldValue("message", "");
                                                }
                                            }}
                                        />
                                    </IconButton>


                                    {/* Text Input */}
                                    <Field
                                        name="message"
                                        component={TextFormField}
                                        placeholder={
                                            selectedFile
                                                ? `Selected: ${selectedFile.name}`
                                                : "Type message..."
                                        }
                                        disabled={!!selectedFile} // disable text if file selected
                                        fullWidth
                                    />

                                    {/* Send Button */}
                                    <Button
                                        variant="contained"
                                        type="submit"
                                        disabled={mutationSendChat.isPending}
                                        style={{ marginTop: "15px" }}
                                    >
                                        {mutationSendChat.isPending ? "Sending" : "Send"}
                                    </Button>
                                </Box>

                                {/* Show selected file */}
                                {selectedFile && (
                                    // <Box
                                    //     mx={0}
                                    //     my={2}
                                    //     display="flex"
                                    //     justifyContent="space-between"
                                    //     alignItems="center"

                                    // >
                                    //     <Typography variant="body2">
                                    //         File: {selectedFile.name}
                                    //     </Typography>

                                    // </Box>
                                    <Box
                                        mx={0}
                                        my={2}
                                        display="flex"
                                        justifyContent="space-between"
                                        alignItems="center"
                                        position="relative" // make Box a positioning context
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: 1,
                                            p: 1,
                                            backgroundColor: "#fafafa",
                                        }}
                                    >
                                        <Typography variant="body2" sx={{ pr: 4 }}>
                                            File: {selectedFile?.name}
                                        </Typography>

                                        <IconButton
                                            size="small"
                                            onClick={() => setSelectedFile(null)}
                                            sx={{
                                                position: "absolute",
                                                top: 4,
                                                right: 4,
                                                color: "grey.600",
                                            }}
                                        >
                                            <CloseIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                )}
                            </Form>
                        )}
                    </Formik>
                </Box>
            )}

            {/* Image Preview Modal */}
            <MainDialogBox 
                open={!!previewImage} 
                handleClose={() => setPreviewImage(null)}
                width="80%"
                padding="10px"
            >
                <Box sx={{ textAlign: "center" }}>
                    {previewImage && (
                        <img 
                            src={previewImage} 
                            alt="Full Screen Preview" 
                            style={{ 
                                maxWidth: "100%", 
                                maxHeight: "85vh", 
                                objectFit: "contain",
                                borderRadius: "4px"
                            }} 
                        />
                    )}
                </Box>
            </MainDialogBox>
        </Box>
    );
};

export default ChatModule;


